/* @ngInject */

export default (
  $rootScope,
  dateTimeService,
  AssetFinanceDetail,
  AssetFinanceSearchProduct,
  AssetFinanceCompareProduct,
  AssetFinanceProposal,
  AssetFinanceProposalAssociatedFee,
  AssetFinanceInsurance,
  AssetFinanceCompliance,
  DocType
) => {
  /*
   * Constructor
   * --------------------------------------------------------------------------------
   */
  function AssetFinance(
    id,
    idOwner,
    idTicket,
    idLenderApplication,
    assetDetails,
    additionalProduct,
    searchFields,
    compareProducts,
    financeProposal,
    commission,
    compliance,
    quickQuoteAssetDetails,
    quickQuoteFinanceProposal,
    quickQuoteSearchFields,
    quickQuoteSelectedProduct,
    isUnsecured,
    isQuickQuoteUnsecured,
    isCommercial,
    timeStamp,
    isActive
  ) {
    this.id = id;
    this.idOwner = idOwner;
    this.idTicket = idTicket;
    this.docType = DocType.of('__datum.assetfinance');
    this.idLenderApplication = idLenderApplication;
    this.assetDetails = assetDetails;
    this.additionalProduct = additionalProduct;
    this.searchFields = searchFields;
    this.compareProducts = compareProducts;
    this.financeProposal = financeProposal;
    this.commission = commission;
    this.compliance = compliance;
    this.quickQuoteAssetDetails = quickQuoteAssetDetails;
    this.quickQuoteFinanceProposal = quickQuoteFinanceProposal;
    this.quickQuoteSearchFields = quickQuoteSearchFields;
    this.quickQuoteSelectedProduct = quickQuoteSelectedProduct;
    this.isUnsecured = isUnsecured;
    this.isQuickQuoteUnsecured = isQuickQuoteUnsecured;
    this.isCommercial = isCommercial;
    this.timeStamp = timeStamp;
    this.isActive = isActive;

    if (this?.financeProposal?.associatedFees?.length) {
      this.financeProposal.associatedFees = _.map(
        _.compact(this.financeProposal.associatedFees),
        (associatedFee) => AssetFinanceProposalAssociatedFee.Build(associatedFee)
      );
    }
  }

  /*
   * Static methods
   * --------------------------------------------------------------------------------
   */
  AssetFinance.blank = function () {
    return AssetFinance.build({
      id: _.uuid(),
      idOwner: '',
      idTicket: '',
      idLenderApplication: '',
      assetDetails: [],
      additionalProduct: {},
      searchFields: {},
      compareProducts: [],
      financeProposal: {},
      commission: {
        idCommission: '',
        commissionSplits: [],
        isEdited: false
      },
      compliance: {},
      quickQuoteAssetDetails: [],
      quickQuoteFinanceProposal: {},
      quickQuoteSearchFields: {},
      quickQuoteSelectedProduct: [],
      isUnsecured: false,
      isQuickQuoteUnsecured: false,
      isCommercial: true,
      timeStamp: dateTimeService.epoch(),
      isActive: true
    });
  };

  AssetFinance.build = function (data) {
    return new AssetFinance(
      data.id ? data.id : _.uuid(),
      data.idOwner || undefined,
      data.idTicket || undefined,
      data.idLenderApplication || undefined,
      _.map(_.compact(data.assetDetails), (assetDetail) =>
        AssetFinanceDetail.Build(assetDetail)
      ),
      _.isEmpty(data.additionalProduct)
        ? AssetFinanceInsurance.Blank()
        : AssetFinanceInsurance.Build(data.additionalProduct),
      _.isEmpty(data.searchFields)
        ? AssetFinanceSearchProduct.Blank()
        : AssetFinanceSearchProduct.Build(data.searchFields),
      _.map(_.compact(data.compareProducts), (compareProduct) =>
        AssetFinanceCompareProduct.Build(compareProduct)
      ),
      _.isEmpty(data.financeProposal)
        ? AssetFinanceProposal.Blank()
        : AssetFinanceProposal.Build(data.financeProposal),
      data.commission
        ? data.commission
        : {
            idCommission: '',
            commissionSplits: [],
            isEdited: false
          },
      _.isEmpty(data.compliance)
        ? AssetFinanceCompliance.Blank()
        : AssetFinanceCompliance.Build(data.compliance),
      _.map(_.compact(data.quickQuoteAssetDetails), (quickQuoteAssetDetail) =>
        AssetFinanceDetail.Build(quickQuoteAssetDetail)
      ),
      _.isEmpty(data.quickQuoteFinanceProposal)
        ? AssetFinanceProposal.Blank()
        : AssetFinanceProposal.Build(data.quickQuoteFinanceProposal),
      _.isEmpty(data.quickQuoteSearchFields)
        ? AssetFinanceSearchProduct.Blank()
        : AssetFinanceSearchProduct.Build(data.quickQuoteSearchFields),
      data.quickQuoteSelectedProduct || [],
      data.isUnsecured || false,
      data.isQuickQuoteUnsecured || false,
      data.isCommercial || false,
      data.timeStamp ? parseInt(data.timeStamp || 0, 10) : dateTimeService.epoch(),
      data.isActive || true
    );
  };

  /*
   * Public methods
   * --------------------------------------------------------------------------------
   */
  AssetFinance.prototype.getDate = function () {
    return dateTimeService.getFormattedDate(this.timeStamp);
  };
  AssetFinance.prototype.getTime = function () {
    return dateTimeService.getFormattedTime(this.timeStamp);
  };
  AssetFinance.prototype.deleteDateObject = function () {
    for (let i = 0; i < this?.assetDetails?.length; i += 1) {
      delete this?.assetDetails[i]?.estimatedComplianceDateObject;
    }
    for (let i = 0; i < this?.quickQuoteAssetDetails?.length; i += 1) {
      delete this?.assetDetails[i]?.estimatedComplianceDateObject;
    }
    delete this?.additionalProduct?.premiumEndDateObject;
    delete this?.compliance?.verifications?.interviewDetailsDateVerifiedObject;
    delete this?.compliance?.verifications
      ?.antiMoneyLaunderingChecksCompletedDateVerifiedObject;
    delete this?.compliance?.verifications?.creditChecksCompletedDateVerifiedObject;
    delete this?.compliance?.verifications?.incomeVerificationCompletedDateVerifiedObject;
    delete this?.compliance?.verifications?.livingExpensesVerifiedDateVerifiedObject;
    delete this?.compliance?.verifications?.assetInspectionDateVerifiedObject;
    delete this?.compliance?.verifications?.completePpsrCheckDateVerifiedObject;
    delete this?.compliance?.verifications?.completeConfirmationOfOwnershipDateVerifiedObject;
    for (let i = 0; i < this?.compliance?.verifications?.applicants?.length; i += 1) {
      delete this.compliance.verifications.applicants[i].dateVerifiedObject;
    }
  };

  return AssetFinance;
};
